import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Section,
  Button,
  TextInput,
  TextArea,
  Search,
  Loading,
  Tile,
  ToastNotification,
  InlineNotification,
} from '@carbon/react';
import { ChevronLeft, ChevronRight, Close, Add, Checkmark, ArrowLeft, ArrowRight, User } from '@carbon/icons-react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import authService from '../services/authService';
import PageContainer from '../components/PageContainer';
import './Events.css';

// Otpremanje medija dešavanja: fajl ide PRAVO u Azure Blob Storage preko SAS URL-a,
// pa 200 MB video ne prolazi kroz API (nema limita tela zahteva, nema zauzete
// konekcije ka bazi dok traje prenos). Tok: upload-url → PUT na blob → confirm.
// Ako prvi korak ne uspe (storage nije podešen ili CORS na storage nalogu nije
// dozvolio PUT), pada se na stari multipart endpoint — upload tada radi kao ranije.
const uploadEventMedia = async (eventId, file) => {
  const contentType = file.type || 'application/octet-stream';

  let target = null;
  try {
    const targetResponse = await axiosInstance.post(`/api/Event/${eventId}/media/upload-url`, {
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
    });
    target = targetResponse.data?.data || targetResponse.data;
  } catch (error) {
    // Namerno tiho: ispod sledi multipart fallback koji daje pravu grešku ako i on padne.
    console.warn('Direktan upload medija nije dostupan, prelazim na multipart:', error?.message);
    target = null;
  }

  if (target?.uploadUrl && target?.blobName) {
    try {
      // NAMERNO običan axios (bez JWT-a i bez interceptora): SAS je autorizacija,
      // a Authorization zaglavlje na storage domenu obara potpis/CORS.
      await axios.put(target.uploadUrl, file, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': target.requiredHeaders?.['Content-Type'] || contentType,
        },
      });
    } catch (error) {
      // PUT nije prošao (CORS/prenos) — blob nije nastao, pa je multipart put bezbedan.
      console.warn('PUT na Blob Storage nije uspeo, prelazim na multipart:', error?.message);
      target = null;
    }

    // Kada je PUT uspeo blob već postoji: potvrda se NE sme ponoviti preko
    // multipart-a (napravila bi duplikat), pa greška potvrde ide dalje kao
    // greška za TAJ fajl (ostali fajlovi se i dalje otpremaju).
    if (target) {
      await axiosInstance.post(`/api/Event/${eventId}/media/confirm`, {
        blobName: target.blobName,
        fileName: file.name,
      });
      return;
    }
  }

  const uploadData = new FormData();
  uploadData.append('file', file);
  await axiosInstance.post(`/api/Event/${eventId}/media`, uploadData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Dozvoljeni tipovi fajlova — MORAJU da se poklapaju sa backend validacijom
// (EventController.AllowedImageExtensions / AllowedVideoExtensions).
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'];

// Isti limiti kao na backendu (EventController.MaxImageSizeBytes / MaxVideoSizeBytes).
const MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024;
const MAX_VIDEO_SIZE_BYTES = 200 * 1024 * 1024;

// Zaštita UI-ja: najviše ovoliko fajlova po jednom dešavanju.
const MAX_MEDIA_FILES = 30;

// MIME filter za native file picker. NAMERNO MIME wildcard umesto liste ekstenzija:
// mobilni picker-i (iOS/Android WebView) pouzdano dozvoljavaju VIŠESTRUKI izbor samo
// kada je accept "image/*,video/*"; lista ekstenzija (.jpg,.mp4,...) ume da vrati
// samo poslednji izabrani fajl. Stvarna provera tipa radi se u mediaFileError().
const MEDIA_ACCEPT = 'image/*,video/*';

const getFileExtension = (file) => {
  const name = (file?.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot);
};

// Vraća poruku greške za fajl ili null kada je fajl prihvatljiv.
// Provera je po ekstenziji (kao na backendu), pa nema situacije da UI prihvati
// .heic/.avi a API ga odbije.
const mediaFileError = (file) => {
  if (!file) return 'fajl nije izabran';

  const extension = getFileExtension(file);
  const isImage = IMAGE_EXTENSIONS.includes(extension);
  const isVideo = VIDEO_EXTENSIONS.includes(extension);

  if (!isImage && !isVideo) {
    return 'nije podržan tip (dozvoljeni su JPG, PNG, WEBP, GIF, BMP i MP4, WEBM, MOV, M4V)';
  }

  if (!file.size) return 'fajl je prazan';
  if (isImage && file.size > MAX_IMAGE_SIZE_BYTES) return 'slika je veća od 15 MB';
  if (isVideo && file.size > MAX_VIDEO_SIZE_BYTES) return 'snimak je veći od 200 MB';

  return null;
};

const isVideoMedia = (file) => {
  const type = (file?.type || '').toLowerCase();
  if (type.startsWith('video/')) return true;
  return VIDEO_EXTENSIONS.includes(getFileExtension(file));
};

// Identitet fajla u listi — isti fajl izabran dva puta ne otprema se dvaput.
const mediaFileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

const formatFileSize = (bytes) => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};


const EMPTY_EVENT_FORM = { title: '', description: '', residentIds: [] };
const EVENT_STEPS = [
  { label: 'Osnovni podaci', description: 'Naslov i opis' },
  { label: 'Korisnici', description: 'Ko je učestvovao' },
  { label: 'Mediji', description: 'Slike i snimci' },
];

// Formatira ISO datum u "dd.MM.yyyy."
const formatEventDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}.`;
};

// Normalizuje dešavanje iz API-ja u oblik koji koristi UI.
// API polja: id, title, description, creationTime, blobs[], residents[]
const normalizeEvent = (raw) => {
  const blobs = Array.isArray(raw?.blobs) ? raw.blobs : [];
  const media = blobs.map((b) => {
    const contentType = (b.contentType || '').toLowerCase();
    const isVideo = contentType.startsWith('video/') || b.mediaType === 1;
    return {
      id: b.id,
      url: b.url,
      contentType,
      isVideo,
    };
  });
  const residents = Array.isArray(raw?.residents) ? raw.residents : [];
  return {
    id: raw?.id,
    title: raw?.title || '',
    description: raw?.description || '',
    date: formatEventDate(raw?.creationTime),
    residents,
    media,
  };
};

const Events = () => {
  const userRole = authService.getUserRole();
  const isAdmin = userRole === 'administrator';

  const [failedImages, setFailedImages] = useState({});
  // Lightbox: { eventId, index } ili null
  const [lightbox, setLightbox] = useState(null);

  // --- Lista dešavanja (server-side) ---
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [eventsPagination, setEventsPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // --- Dodavanje dešavanja ---
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_EVENT_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const [residents, setResidents] = useState([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [residentsPagination, setResidentsPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [residentSearch, setResidentSearch] = useState('');
  const [residentPage, setResidentPage] = useState(1);
  const [residentPageSize] = useState(5);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaError, setMediaError] = useState(null);
  // { current, total, fileName } dok traje otpremanje medija, inače null.
  const [uploadProgress, setUploadProgress] = useState(null);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const mediaInputRef = useRef(null);

  const totalItems = eventsPagination.totalCount;
  const totalPages = eventsPagination.totalPages;
  const pagedEvents = events;
  const hasNextPage = eventsPagination.hasNextPage;
  const hasPreviousPage = eventsPagination.hasPreviousPage;

  const fetchEvents = async (pageNum = page) => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const params = new URLSearchParams();
      params.append('pageNumber', String(pageNum));
      params.append('pageSize', String(pageSize));
      const response = await axiosInstance.get(`/api/Event?${params.toString()}`);

      const payload = response.data;
      let raw = [];
      if (Array.isArray(payload?.data)) raw = payload.data;
      else if (Array.isArray(payload)) raw = payload;
      else if (Array.isArray(payload?.data?.$values)) raw = payload.data.$values;
      else if (Array.isArray(payload?.$values)) raw = payload.$values;

      setEvents(raw.map(normalizeEvent));

      const pagination = payload?.pagination;
      if (pagination) {
        setEventsPagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
        });
      } else {
        const tp = Math.max(1, Math.ceil(raw.length / pageSize));
        setEventsPagination({
          totalCount: raw.length,
          totalPages: tp,
          hasNextPage: pageNum < tp,
          hasPreviousPage: pageNum > 1,
        });
      }
    } catch (err) {
      console.error('Greška pri učitavanju dešavanja:', err);
      setEventsError('Nije moguće učitati dešavanja. Pokušajte ponovo.');
      setEvents([]);
      setEventsPagination({ totalCount: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false });
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const nextPage = () => {
    if (hasNextPage) setPage((prev) => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    if (hasPreviousPage) setPage((prev) => Math.max(prev - 1, 1));
  };

  const activeEvent = lightbox ? events.find((e) => e.id === lightbox.eventId) : null;
  const activeImages = activeEvent ? activeEvent.media : [];

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const showPrev = useCallback(() => {
    setLightbox((prev) => {
      if (!prev) return prev;
      const event = events.find((e) => e.id === prev.eventId);
      if (!event) return prev;
      const count = event.media.length;
      return { ...prev, index: (prev.index - 1 + count) % count };
    });
  }, [events]);

  const showNext = useCallback(() => {
    setLightbox((prev) => {
      if (!prev) return prev;
      const event = events.find((e) => e.id === prev.eventId);
      if (!event) return prev;
      const count = event.media.length;
      return { ...prev, index: (prev.index + 1) % count };
    });
  }, [events]);

  // Tastatura: Esc zatvara, strelice menjaju sliku
  useEffect(() => {
    if (!lightbox) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, closeLightbox, showPrev, showNext]);

  const markFailed = (key) =>
    setFailedImages((prev) => ({ ...prev, [key]: true }));

  // --- Dodavanje dešavanja: logika ---
  const showToast = (kind, title, subtitle, duration = 3000) => {
    setToast({ show: true, kind, title, subtitle });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), duration);
  };

  const fetchResidents = async (search = residentSearch, pageNum = residentPage) => {
    try {
      setResidentsLoading(true);
      const params = new URLSearchParams();
      params.append('pageNumber', String(pageNum));
      params.append('pageSize', String(residentPageSize));
      if (search) {
        params.append('search', search);
      }
      const response = await axiosInstance.get(`/api/Resident?${params.toString()}`);

      // Normalizacija različitih formata odgovora
      let data = [];
      const payload = response.data;
      if (Array.isArray(payload)) {
        data = payload;
      } else if (payload && typeof payload === 'object') {
        if (Array.isArray(payload.value)) data = payload.value;
        else if (Array.isArray(payload.$values)) data = payload.$values;
        else if (Array.isArray(payload.items)) data = payload.items;
        else if (Array.isArray(payload.data)) data = payload.data;
        else if (payload.data && typeof payload.data === 'object') {
          if (Array.isArray(payload.data.$values)) data = payload.data.$values;
          else if (Array.isArray(payload.data.items)) data = payload.data.items;
          else if (Array.isArray(payload.data.value)) data = payload.data.value;
        }
      }
      setResidents(data);

      // Paginacija (server-side metadata ako postoji, inače lokalna procena)
      const pagination = payload?.pagination || payload?.data?.pagination;
      if (pagination) {
        setResidentsPagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false,
        });
      } else {
        const totalPages = Math.max(1, Math.ceil(data.length / residentPageSize));
        setResidentsPagination({
          totalCount: data.length,
          totalPages,
          hasNextPage: pageNum < totalPages,
          hasPreviousPage: pageNum > 1,
        });
      }
    } catch (err) {
      console.error('Greška pri učitavanju korisnika:', err);
      setResidents([]);
      setResidentsPagination({ totalCount: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false });
    } finally {
      setResidentsLoading(false);
    }
  };

  // Server-side paginacija liste korisnika (5 po strani)
  const residentTotalPages = residentsPagination.totalPages;
  const pagedResidents = residents;
  const hasNextResidentPage = residentsPagination.hasNextPage;
  const hasPrevResidentPage = residentsPagination.hasPreviousPage;

  const nextResidentPage = () => {
    if (hasNextResidentPage) {
      const next = residentPage + 1;
      setResidentPage(next);
      fetchResidents(residentSearch, next);
    }
  };

  const prevResidentPage = () => {
    if (hasPrevResidentPage) {
      const prev = residentPage - 1;
      setResidentPage(prev);
      fetchResidents(residentSearch, prev);
    }
  };

  const handleResidentSearch = (value) => {
    setResidentSearch(value);
    setResidentPage(1);
    fetchResidents(value, 1);
  };

  const toggleResident = (resident) => {
    const id = resident.id ?? resident.residentId;
    setFormData((prev) => {
      const exists = prev.residentIds.includes(id);
      return {
        ...prev,
        residentIds: exists
          ? prev.residentIds.filter((r) => r !== id)
          : [...prev.residentIds, id],
      };
    });
  };

  // Briše započeto dešavanje (forma + izabrani mediji). Poziva se posle uspešnog
  // čuvanja i na eksplicitni "Odbaci".
  const resetDraft = () => {
    setFormData(EMPTY_EVENT_FORM);
    setFormErrors({});
    setFormError(null);
    setMediaFiles([]);
    setMediaError(null);
    setUploadProgress(null);
    setCurrentStep(0);
    setStepTransition('entering');
    setResidentSearch('');
    setResidentPage(1);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const openCreatePanel = () => {
    // Započeto dešavanje se NE briše: ako korisnik zatvori panel i vrati se, već
    // izabrani mediji i uneti podaci ostaju. Ranije je otvaranje panela resetovalo
    // formu, pa je izbor medija tiho gubio SVE prethodno izabrane fajlove
    // (npr. izabere 1 fajl, zatvori panel, doda još 2 — otpremila bi se samo ta 2).
    setFormErrors({});
    setFormError(null);
    setMediaError(null);
    setUploadProgress(null);
    setCurrentStep(0);
    setStepTransition('entering');
    setIsSidePanelOpen(true);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
    fetchResidents(residentSearch, residentPage);
  };

  const closeSidePanel = () => {
    // Namerno NE briše draft — izbor medija i uneti podaci se čuvaju dok se
    // dešavanje ne sačuva ili korisnik ne klikne "Odbaci".
    setIsSidePanelOpen(false);
    setFormError(null);
    setMediaError(null);
    setUploadProgress(null);
    setCurrentStep(0);
    setStepTransition('entering');
  };

  // Postoji li započeto dešavanje (koristi se za prikaz dugmeta "Odbaci").
  const hasDraftContent =
    formData.title.trim().length > 0 ||
    formData.description.trim().length > 0 ||
    formData.residentIds.length > 0 ||
    mediaFiles.length > 0;

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return formData.title.trim() && formData.description.trim();
      default:
        return true; // Korisnici i mediji su opcioni
    }
  };

  const nextStep = () => {
    if (currentStep < EVENT_STEPS.length - 1 && isStepValid()) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleStepClick = (targetStep) => {
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }
    if (targetStep === currentStep) return;
    if (targetStep > currentStep + 1) return;
    if (!isStepValid()) return;
    goToStep(targetStep);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Dodaje nove fajlove u listu (postojeći izbor se NE briše) — tako korisnik može
  // da izabere više fotografija i videa odjednom, ili da ih dodaje u više navrata.
  const addMediaFiles = (incoming) => {
    const files = Array.from(incoming || []);
    if (files.length === 0) return;

    const errors = [];
    const accepted = [];
    const existingKeys = new Set(mediaFiles.map(mediaFileKey));

    files.forEach((file) => {
      const error = mediaFileError(file);
      if (error) {
        errors.push(`${file.name}: ${error}.`);
        return;
      }

      const key = mediaFileKey(file);
      if (existingKeys.has(key)) return; // već je u listi — preskoči tiho

      if (mediaFiles.length + accepted.length >= MAX_MEDIA_FILES) {
        errors.push(`Najviše ${MAX_MEDIA_FILES} fajlova po dešavanju.`);
        return;
      }

      existingKeys.add(key);
      accepted.push(file);
    });

    if (accepted.length > 0) {
      setMediaFiles((prev) => [...prev, ...accepted]);
    }

    setMediaError(errors.length > 0 ? errors.join(' ') : null);
  };

  const handleMediaChange = (e) => {
    const selected = Array.from(e.target.files || []);
    // Reset native input-a da bi isti fajl mogao ponovo da se izabere
    // (npr. posle uklanjanja iz liste).
    if (mediaInputRef.current) mediaInputRef.current.value = '';
    addMediaFiles(selected);
  };

  const removeMediaFile = (index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaError(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Naslov je obavezan.';
    } else if (formData.title.trim().length > 200) {
      errors.title = 'Naslov ne može biti duži od 200 karaktera.';
    }
    if (!formData.description.trim()) {
      errors.description = 'Opis je obavezan.';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormLoading(true);
    setFormError(null);

    const payload = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      residentIds: formData.residentIds,
    };

    try {
      const response = await axiosInstance.post('/api/Event', payload);
      const created = response.data?.data || response.data;
      const eventId = created?.id ?? created?.eventId;

      // Upload medija (slike i snimci) za novo dešavanje.
      // Svaki fajl se otprema POJEDINAČNO i greška jednog fajla NE prekida
      // otpremanje ostalih (ranije je prva greška prekidala celu petlju, pa je
      // završavao samo deo izabranih fajlova).
      const failedFiles = [];
      const filesToUpload = mediaFiles;

      if (eventId && filesToUpload.length > 0) {
        for (let index = 0; index < filesToUpload.length; index += 1) {
          const file = filesToUpload[index];
          setUploadProgress({ current: index + 1, total: filesToUpload.length, fileName: file.name });
          try {
            // eslint-disable-next-line no-await-in-loop
            await uploadEventMedia(eventId, file);
          } catch (uploadError) {
            console.error(`Greška pri otpremanju medija "${file.name}":`, uploadError);
            failedFiles.push(file.name);
          }
        }
        setUploadProgress(null);
      }

      setIsSidePanelOpen(false);
      // Draft se briše SAMO posle uspešnog čuvanja — novi "Dodaj dešavanje"
      // kreće od prazne forme, bez medija prethodnog dešavanja.
      resetDraft();

      if (failedFiles.length > 0) {
        showToast(
          'warning',
          'Delimičan uspeh',
          `Dešavanje je sačuvano, ali ${failedFiles.length} od ${filesToUpload.length} fajlova nije otpremljeno: ${failedFiles.join(', ')}.`,
          6000
        );
      } else {
        showToast('success', 'Uspešno!', `Dešavanje "${payload.title}" je uspešno dodato.`);
      }
      setPage(1);
      fetchEvents(1);
    } catch (err) {
      console.error('Greška pri čuvanju dešavanja:', err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Nije moguće sačuvati dešavanje. Pokušajte ponovo.';
      setFormError(msg);
      showToast('error', 'Greška!', msg, 4000);
    } finally {
      setUploadProgress(null);
      setFormLoading(false);
    }
  };

  return (
    <PageContainer>
      <Section>
        {isAdmin && (
          <div className="events-toolbar">
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={openCreatePanel}
              className="events-add-button"
            >
              Dodaj dešavanje
            </Button>
          </div>
        )}

        {eventsLoading ? (
          <div className="events-loading">
            <Loading description="Učitavanje dešavanja..." withOverlay={false} small />
          </div>
        ) : eventsError ? (
          <div className="events-error">
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={eventsError}
              onCloseButtonClick={() => setEventsError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        ) : totalItems === 0 ? (
          <p className="events-empty">Trenutno nema dešavanja.</p>
        ) : (
          <div className="events-feed">
            {pagedEvents.map((event) => {
              const visibleMedia = event.media.filter(
                (_, i) => !failedImages[`${event.id}-${i}`]
              );
              return (
                <article className="event-card" key={event.id}>
                  <header className="event-card__header">
                    <h3 className="event-card__title">{event.title}</h3>
                    <div className="event-card__meta">
                      <span>{event.date}</span>
                      {event.residents.length > 0 && (
                        <>
                          <span className="event-card__dot">·</span>
                          <span>
                            {event.residents.length}{' '}
                            {event.residents.length === 1 ? 'učesnik' : 'učesnika'}
                          </span>
                        </>
                      )}
                    </div>
                  </header>

                  {/* Galerija medija (slike i snimci) */}
                  {visibleMedia.length > 0 && (
                    <div
                      className={`event-card__gallery event-card__gallery--${Math.min(
                        event.media.length,
                        4
                      )}`}
                    >
                      {event.media.map((item, index) => {
                        if (failedImages[`${event.id}-${index}`]) return null;
                        return (
                          <button
                            type="button"
                            className="event-card__thumb"
                            key={item.id || `${event.id}-${index}`}
                            onClick={() => setLightbox({ eventId: event.id, index })}
                            aria-label={`Otvori ${item.isVideo ? 'snimak' : 'sliku'} ${index + 1} od ${event.media.length}`}
                          >
                            {item.isVideo ? (
                              <>
                                <video
                                  src={item.url}
                                  muted
                                  preload="metadata"
                                  onError={() => markFailed(`${event.id}-${index}`)}
                                />
                                <span className="event-card__play" aria-hidden="true">▶</span>
                              </>
                            ) : (
                              <img
                                src={item.url}
                                alt={`${event.title} - slika ${index + 1}`}
                                loading="lazy"
                                onError={() => markFailed(`${event.id}-${index}`)}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <p className="event-card__description">{event.description}</p>
                </article>
              );
            })}
          </div>
        )}

        {!eventsLoading && !eventsError && totalItems > pageSize && (
          <div className="events-pagination">
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronLeft}
              onClick={prevPage}
              hasIconOnly
              disabled={!hasPreviousPage}
              iconDescription="Prethodna stranica"
            />
            <span className="events-pagination-label">
              {page}/{totalPages}
            </span>
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronRight}
              onClick={nextPage}
              hasIconOnly
              disabled={!hasNextPage}
              iconDescription="Sledeća stranica"
            />
          </div>
        )}
      </Section>

      {/* Lightbox */}
      {lightbox && activeEvent && (
        <div
          className="events-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={activeEvent.title}
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="events-lightbox__close"
            onClick={closeLightbox}
            aria-label="Zatvori"
          >
            <Close size={24} />
          </button>

          {activeImages.length > 1 && (
            <button
              type="button"
              className="events-lightbox__nav events-lightbox__nav--prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              aria-label="Prethodna slika"
            >
              <ChevronLeft size={28} />
            </button>
          )}

          <figure
            className="events-lightbox__figure"
            onClick={(e) => e.stopPropagation()}
          >
            {activeImages[lightbox.index]?.isVideo ? (
              <video
                src={activeImages[lightbox.index].url}
                controls
                autoPlay
                className="events-lightbox__video"
              />
            ) : (
              <img
                src={activeImages[lightbox.index]?.url}
                alt={`${activeEvent.title} - slika ${lightbox.index + 1}`}
              />
            )}
            <figcaption>
              {activeEvent.title}
              <span className="events-lightbox__counter">
                {lightbox.index + 1} / {activeImages.length}
              </span>
            </figcaption>
          </figure>

          {activeImages.length > 1 && (
            <button
              type="button"
              className="events-lightbox__nav events-lightbox__nav--next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              aria-label="Sledeća slika"
            >
              <ChevronRight size={28} />
            </button>
          )}
        </div>
      )}

      {/* Right side panel - dodavanje dešavanja */}
      <div className={`event-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Dodaj dešavanje</h2>
          <div className="side-panel-header-actions">
            {hasDraftContent && (
              <Button
                kind="ghost"
                size="sm"
                type="button"
                onClick={resetDraft}
                disabled={formLoading}
              >
                Odbaci
              </Button>
            )}
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={closeSidePanel}
              disabled={formLoading}
              iconDescription="Zatvori"
            />
          </div>
        </div>

        {formError && (
          <div style={{ padding: '1rem 1.5rem 0' }}>
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={formError}
              onCloseButtonClick={() => setFormError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        )}

        <div className="side-panel-content">
          <form onSubmit={handleSubmit} className="event-form">
            {/* Progress bar */}
            <div className="event-progress-bar">
              <div className="progress-steps">
                {EVENT_STEPS.map((step, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <div className={`progress-line ${currentStep >= index ? 'completed' : ''}`} />
                    )}
                    <div
                      className={`progress-step ${currentStep >= index ? 'completed' : ''} ${currentStep === index ? 'active' : ''}`}
                      onClick={() => handleStepClick(index)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="step-circle">
                        {currentStep > index ? <Checkmark size={16} /> : index + 1}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className={`step-content ${stepTransition}`}>
              <Tile style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '500' }}>
                  {EVENT_STEPS[currentStep].label}
                </h3>

                {currentStep === 0 && (
                  <>
                    <TextInput
                      id="event-title"
                      labelText="Naslov"
                      placeholder="Unesite naslov dešavanja"
                      value={formData.title}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                      invalid={!!formErrors.title}
                      invalidText={formErrors.title}
                      disabled={formLoading}
                    />

                    <TextArea
                      id="event-description"
                      labelText="Opis"
                      placeholder="Unesite opis dešavanja"
                      value={formData.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      invalid={!!formErrors.description}
                      invalidText={formErrors.description}
                      disabled={formLoading}
                      rows={4}
                      maxLength={2000}
                    />
                  </>
                )}

                {currentStep === 1 && (
                  <div className="event-residents-step">
                    <Search
                      size="lg"
                      placeholder="Pretražite korisnike..."
                      value={residentSearch}
                      onChange={(e) => handleResidentSearch(e.target.value)}
                      onClear={() => handleResidentSearch('')}
                      labelText=""
                      closeButtonLabelText="Obriši pretragu"
                    />

                    <div className="event-residents-header">
                      <span className="event-residents-count">
                        Izabrano: {formData.residentIds.length}
                      </span>
                      <div className="event-residents-pagination">
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={ChevronLeft}
                          onClick={prevResidentPage}
                          hasIconOnly
                          disabled={!hasPrevResidentPage}
                          iconDescription="Prethodna stranica"
                        />
                        <span className="event-residents-page">
                          {residentPage}/{residentTotalPages}
                        </span>
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={ChevronRight}
                          onClick={nextResidentPage}
                          hasIconOnly
                          disabled={!hasNextResidentPage}
                          iconDescription="Sledeća stranica"
                        />
                      </div>
                    </div>

                    {residentsLoading ? (
                      <div className="event-residents-loading">
                        <Loading description="Učitavanje korisnika..." withOverlay={false} small />
                      </div>
                    ) : pagedResidents.length > 0 ? (
                      <div className="event-residents-list">
                        {pagedResidents.map((resident) => {
                          const id = resident.id ?? resident.residentId;
                          const isSelected = formData.residentIds.includes(id);
                          const fullName =
                            resident.fullName ||
                            `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
                          const initials =
                            `${resident.firstName?.[0] || ''}${resident.lastName?.[0] || ''}`.toUpperCase() || '?';
                          const hasImage = resident.imgSrc && resident.imgSrc !== '';
                          return (
                            <div
                              key={id}
                              className={`event-resident-item ${isSelected ? 'event-resident-selected' : ''}`}
                              onClick={() => toggleResident(resident)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleResident(resident);
                                }
                              }}
                            >
                              <div className="event-resident-avatar">
                                {hasImage ? (
                                  <>
                                    <img
                                      src={resident.imgSrc}
                                      alt={fullName}
                                      className="event-resident-image"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="event-resident-initials" style={{ display: 'none' }}>
                                      {initials}
                                    </div>
                                  </>
                                ) : (
                                  <div className="event-resident-initials">{initials}</div>
                                )}
                              </div>
                              <div className="event-resident-info">
                                <span className="event-resident-name">{fullName}</span>
                                <span className="event-resident-meta">
                                  {resident.room?.name ? `Soba ${resident.room.name}` : ''}
                                  {resident.birthDate
                                    ? `${resident.room?.name ? ' • ' : ''}${new Date(resident.birthDate).toLocaleDateString('sr-Latn-RS')}`
                                    : ''}
                                </span>
                              </div>
                              <div className={`event-resident-check ${isSelected ? 'checked' : ''}`}>
                                {isSelected && <Checkmark size={16} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="event-residents-empty">
                        <User size={24} />
                        <span>
                          {residentSearch
                            ? 'Nema korisnika koji odgovaraju pretrazi'
                            : 'Nema dostupnih korisnika'}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="event-media-field">
                    <label className="event-media-label" htmlFor="event-media-input">
                      Slike i snimci (opciono)
                    </label>

                    <p className="event-media-help">
                      Možete izabrati više slika i snimaka odjednom ili ih dodavati u više
                      navrata. Dozvoljeni su JPG, PNG, WEBP, GIF, BMP (do 15 MB) i MP4, WEBM,
                      MOV, M4V (do 200 MB).
                    </p>

                    <div className="event-media-actions">
                      <Button
                        kind="tertiary"
                        size="md"
                        type="button"
                        onClick={() => mediaInputRef.current?.click()}
                        disabled={formLoading || mediaFiles.length >= MAX_MEDIA_FILES}
                      >
                        Izaberi slike i snimke
                      </Button>
                      {mediaFiles.length > 0 && (
                        <span className="event-media-hint">
                          Izabrano fajlova: {mediaFiles.length}
                        </span>
                      )}
                    </div>

                    {/* Native input sa multiple + MIME accept: jedini pouzdan način da
                        i mobilni picker-i dozvole izbor više fotografija i videa. */}
                    <input
                      id="event-media-input"
                      ref={mediaInputRef}
                      className="event-media-input"
                      type="file"
                      accept={MEDIA_ACCEPT}
                      multiple
                      disabled={formLoading}
                      onChange={handleMediaChange}
                    />

                    {mediaError && (
                      <p className="event-media-error">{mediaError}</p>
                    )}

                    {mediaFiles.length > 0 && (
                      <ul className="event-media-list">
                        {mediaFiles.map((file, index) => (
                          <li className="event-media-item" key={mediaFileKey(file)}>
                            <span
                              className={`event-media-item__badge ${isVideoMedia(file) ? 'is-video' : 'is-image'}`}
                              aria-hidden="true"
                            >
                              {isVideoMedia(file) ? 'VIDEO' : 'SLIKA'}
                            </span>
                            <span className="event-media-item__name" title={file.name}>
                              {file.name}
                            </span>
                            <span className="event-media-item__size">
                              {formatFileSize(file.size)}
                            </span>
                            <button
                              type="button"
                              className="event-media-item__remove"
                              onClick={() => removeMediaFile(index)}
                              disabled={formLoading}
                              aria-label={`Ukloni ${file.name}`}
                            >
                              <Close size={16} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {uploadProgress && (
                      <p className="event-media-progress" role="status">
                        Otpremanje {uploadProgress.current}/{uploadProgress.total}:{' '}
                        {uploadProgress.fileName}
                      </p>
                    )}
                  </div>
                )}
              </Tile>
            </div>

            <div className="form-actions">
              <Button
                kind="secondary"
                type="button"
                onClick={prevStep}
                disabled={formLoading || currentStep === 0}
                renderIcon={ArrowLeft}
              >
                Nazad
              </Button>
              {currentStep < EVENT_STEPS.length - 1 ? (
                <Button
                  kind="primary"
                  type="button"
                  renderIcon={ArrowRight}
                  onClick={nextStep}
                  disabled={!isStepValid()}
                >
                  Sledeći korak
                </Button>
              ) : (
                <Button
                  kind="primary"
                  type="submit"
                  renderIcon={Checkmark}
                  disabled={formLoading}
                >
                  {uploadProgress
                    ? `Otpremanje ${uploadProgress.current}/${uploadProgress.total}...`
                    : formLoading
                      ? 'Čuvanje...'
                      : 'Sačuvaj'}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Side panel overlay */}
      {isSidePanelOpen && (
        <div
          className="side-panel-overlay"
          onClick={() => {
            // Dok traje čuvanje/otpremanje panel se ne zatvara na klik van njega.
            if (!formLoading) closeSidePanel();
          }}
        />
      )}

      {/* Toast notification */}
      {toast.show && (
        <div className="toast-container">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            timeout={0}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
            lowContrast
          />
        </div>
      )}

      {/* Floating Action Button (mobile only) */}
      {isAdmin && !isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button events-fab"
          onClick={openCreatePanel}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Add size={24} style={{ flexShrink: 0, fill: '#ffffff' }} color="#ffffff" />
          <span style={{ color: 'white', fontWeight: '500', fontSize: '0.95rem', whiteSpace: 'nowrap', userSelect: 'none' }}>
            Dodaj dešavanje
          </span>
        </button>,
        document.body
      )}
    </PageContainer>
  );
};

export default Events;
